import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { Button } from './ui/button'
import { useAuth } from '../context/AuthContext'
import ClickProLogo from './ClickProLogo'

const NAV_ITEMS = [
  { path: 'https://clicketpro.fr', label: 'Accueil', key: 'home', external: true },
  { path: 'https://clicketpro.fr/artisans', label: 'Artisans', key: 'artisans', external: true },
  { path: '/demandes', label: 'Demandes', key: 'demandes' },
  { path: 'https://clicketpro.fr/emplois', label: "Offres d'emploi", key: 'emplois', external: true },
]

const getNavLinkClass = (key, activePath) =>
  activePath === key
    ? 'text-primary font-semibold'
    : 'text-muted-foreground hover:text-foreground font-medium transition-colors'

export default function PublicPageHeader({ activePath = '', fixed = false }) {
  const { loading: authLoading, isAuthenticated } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const positionClass = fixed
    ? 'fixed top-0 left-0 right-0'
    : 'sticky top-0'

  const authActions = authLoading ? (
    <div className="h-9 w-28 animate-pulse rounded-md bg-muted" />
  ) : isAuthenticated ? (
    <Link to="/tableau-de-bord">
      <Button className="btn-clickpro-primary" data-testid="dashboard-btn">
        Mon espace
      </Button>
    </Link>
  ) : (
    <>
      <Link to="/connexion">
        <Button variant="ghost" className="font-medium text-muted-foreground hover:text-foreground" data-testid="login-btn">
          Connexion
        </Button>
      </Link>
      <Link to="/inscription">
        <Button className="btn-clickpro-primary" data-testid="register-btn">
          Devenir partenaire
        </Button>
      </Link>
    </>
  )

  return (
    <header className={`${positionClass} z-50 bg-white/90 backdrop-blur-md border-b border-border/60 shadow-header`}>
      <div className="container-clickpro">
        <div className="flex items-center justify-between h-[4.25rem] md:h-[4.75rem]">
          <a href="https://clicketpro.fr" data-testid="logo">
            <ClickProLogo />
          </a>

          <nav className="hidden md:flex items-center gap-10">
            {NAV_ITEMS.map(({ path, label, key, external }) =>
              external ? (
                <a key={key} href={path} className={getNavLinkClass(key, activePath)}>
                  {label}
                </a>
              ) : (
                <Link key={key} to={path} className={getNavLinkClass(key, activePath)}>
                  {label}
                </Link>
              )
            )}
          </nav>

          <div className="hidden md:flex items-center gap-3">{authActions}</div>

          <button
            type="button"
            className="md:hidden p-2 rounded-md hover:bg-muted transition-colors"
            onClick={() => setMobileMenuOpen((open) => !open)}
            aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-border py-4 animate-fade-in">
          <div className="container-clickpro flex flex-col gap-1">
            {NAV_ITEMS.map(({ path, label, key, external }) =>
              external ? (
                <a
                  key={key}
                  href={path}
                  className={`px-4 py-3 rounded-md hover:bg-muted ${getNavLinkClass(key, activePath)}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {label}
                </a>
              ) : (
                <Link
                  key={key}
                  to={path}
                  className={`px-4 py-3 rounded-md hover:bg-muted ${getNavLinkClass(key, activePath)}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {label}
                </Link>
              )
            )}
            <div className="flex flex-col gap-2 pt-4 mt-2 border-t border-border">
              {authLoading ? (
                <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
              ) : isAuthenticated ? (
                <Link to="/tableau-de-bord" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full btn-clickpro-primary">Mon espace</Button>
                </Link>
              ) : (
                <>
                  <Link to="/connexion" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">Connexion</Button>
                  </Link>
                  <Link to="/inscription" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full btn-clickpro-primary">Devenir partenaire</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}

export { ClickProLogo }
